import { Button } from "@components/common/button"
import { DictType } from "@pma/types"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/common/dialog"
import axios from "axios"
import { parseCookies } from "nookies"
import { useMutation, useQuery } from "@tanstack/react-query"
import { toast } from "@hooks/use-toast"
import { ACCESS_TOKEN, queryClient } from "@pma/utils/constants"
import { FOLDER_ENDPOINT, MOVE_FILE_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes"
import { ArrowLeft, ChevronRight, FolderIcon, FolderSearch } from "lucide-react"
import useTranslator from "@hooks/use-translator"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useEffect, useState } from "react"
import PmsLoader from "../common/loader"

interface Props {
  file: DictType | undefined
  fileArr: DictType[] | []
  parentFolder: DictType
  rootFolder: any
  handleBatchSelection: () => void
  resetPageParam: () => void
  isMenuView?: boolean
  setCutFiles: React.Dispatch<React.SetStateAction<DictType[]>>
  setFolderItems: React.Dispatch<React.SetStateAction<{ [id: number]: any[] }>>
  foldersMeta: { [id: number]: number }
  folderItems: { [id: number]: any[] }
  cutFiles: DictType[]
  handleTreeItemFolderClick: (item: DictType, forceExpand: boolean) => void
  setFoldersMeta: React.Dispatch<React.SetStateAction<{ [id: number]: number }>>
  moveFileModal: boolean
  setMoveFileModal: React.Dispatch<React.SetStateAction<boolean>>
  setBreadcrumbs: React.Dispatch<React.SetStateAction<{ [id: number]: any[] }>>
  breadcrumbs: { [id: number]: any[] }
  setActiveFolder: React.Dispatch<React.SetStateAction<any>>
}
const MoveFile = ({
  file,
  fileArr = [],
  resetPageParam,
  handleBatchSelection,
  parentFolder,
  rootFolder,
  isMenuView = false,
  setCutFiles,
  setFolderItems,
  foldersMeta,
  folderItems,
  cutFiles,
  handleTreeItemFolderClick,
  setFoldersMeta,
  moveFileModal,
  setMoveFileModal,
  setBreadcrumbs,
  breadcrumbs,
  setActiveFolder
}: Props) => {
  const [selectedFolder, setSelectedFolder] = useState<DictType>({})
  const { translate, } = useTranslator()
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const [searchParam] = useSearchParams()
  const newParams = new URLSearchParams(searchParam)
  const [currentFolder, setCurrentFolder] = useState<DictType | null>(null)
  const [moveBreadcrumbs, setMoveBreadcrumbs] = useState<DictType[]>([])
  const [originalBreadcrumbs, setOriginalBreadcrumbs] = useState<{ [id: number]: any[] }>(breadcrumbs)
  const [modalFolderItem, setModalFolderItem] = useState<{ [id: number]: any[] }>(folderItems)
  const navigate = useNavigate()

  const moveFile = async () => {
    if (fileArr && fileArr?.length > 0) {
      const idArr = fileArr.map((val) => val.id)
      const response = await axios.post(
        SERVER_URL + MOVE_FILE_ENDPOINT,
        {
          files_id: idArr,
          folder_id: selectedFolder?.id,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      )

      return response.data
    } else if (file) {
      const response = await axios.post(
        SERVER_URL + MOVE_FILE_ENDPOINT,
        {
          files_id: [file.id],
          folder_id: selectedFolder?.id,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      )

      return response.data
    }
  }

  const {
    data: subFolderContent,
    isLoading: isLoadingSubFolder,
    isFetched: isFetchedSubFolders,
    isFetching: isFetchingSubFolders,
    refetch: refetchSubFolders,
  } = useQuery({
    queryKey: ["sub-folder"],
    queryFn: () => getFolderContent(parentFolder?.id),
    enabled: false,
  })

  const getFolderContent = async (folderId: number) => {
    const response = await axios.post(`${SERVER_URL}${FOLDER_ENDPOINT}`, { folder_id: folderId }, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })

    return response.data
  }

  const {
    data: currentFolderContent,
    isLoading: isLoadingCurrent,
    isFetched: isFetchedCurrent,
    isFetching: isFetchingCurrent,
    refetch: refetchCurrent,
  } = useQuery({
    queryKey: ["current-folder", currentFolder?.id],
    queryFn: () => getFolderContent(currentFolder?.id),
    enabled: moveFileModal && !!currentFolder?.id,
  })

  const refetchFolderContent = (folderId: number) => {
    queryClient.invalidateQueries({
      queryKey: [{ id: folderId }, null, "get-curr-folder-files"],
    })
  }

  const moveFileMutation = useMutation({
    mutationKey: ["move-file", file?.id],
    mutationFn: moveFile,
    onSuccess: (data) => {
      const fileIds = [file?.id]
      const sourceFolderIds = new Set<number>()
      let singleFileName: string | undefined
      if (fileIds.length === 1) {
        singleFileName = cutFiles.find((file) => file.id === fileIds[0])?.name
      }

      fileIds.forEach((fileId: number) => {
        const sourceFolder = foldersMeta[fileId]
        if (sourceFolder) {
          sourceFolderIds.add(sourceFolder)
        }
      })

      sourceFolderIds.forEach((sourceFolder) => {
        setFolderItems((prev) => ({
          ...prev,
          [sourceFolder]: prev[sourceFolder].filter((item) => !fileIds.includes(item.id)),
        }))
        refetchFolderContent(sourceFolder)
      })

      if (fileArr && fileArr.length > 0) handleBatchSelection()

      const targetFolderId = selectedFolder?.id
      const targetFolder = Object.values(modalFolderItem)
        .flat()
        .find((item) => item.id === targetFolderId && item.as === "folder")
      if (targetFolderId) refetchFolderContent(targetFolderId)
      if (targetFolder) handleTreeItemFolderClick(targetFolder, true)
      if (data.id == rootFolder.id) handleTreeItemFolderClick(data, true)

      resetPageParam()
      toast({
        title: translate("Success"),
        description: fileArr && fileArr.length > 0
          ? `${fileArr.length} ${translate(fileArr.length === 1 ? "file" : "files")} ${translate("have been moved")}`
          : `${file?.name} ${translate("has been moved")}`,
        variant: "success",
      })
      setCutFiles([])
      newParams.set("revalidate", "projects")
      navigate(`?${newParams.toString()}`)
      setMoveFileModal(!moveFileModal)
    },
    onError: (error) => {
      toast({
        title: translate("Error"),
        description: fileArr && fileArr.length > 0
          ? `${translate("Unable to move")} ${fileArr.length} ${translate(fileArr.length === 1 ? "file" : "files")}`
          : `${translate("Unable to move")} ${file?.name}`,
        variant: "error",
      })
    },
  })

  function expandPathUpUntilRoot(data: any, folderId: number) {
    const updated = JSON.parse(JSON.stringify(data)); // deep copy
    const visitedIds = new Set<number>(); // To avoid infinite loops

    let currentId: number | undefined = folderId;

    while (currentId !== undefined && !visitedIds.has(currentId)) {
      visitedIds.add(currentId);
      let found = false;

      for (const sectionKey in updated) {
        for (const item of updated[sectionKey]) {
          if (item.id === currentId && item.as === "folder") {
            // Only set isExpanded to true if it's not already true
            if (item.isExpanded !== true) {
              item.isExpanded = true;
            }

            if (item.root === true || item.parent_id == null) {
              currentId = undefined;
            } else {
              currentId = item.parent_id;
            }

            found = true;
            break;
          }
        }
        if (found) break;
      }

      if (!found) break;
    }

    return updated;
  }

  const handleClick = () => {
    if (fileArr.length == 0) {
      const activeFolder = file?.folders_id;
      const newFolderItems = { ...modalFolderItem, [activeFolder]: modalFolderItem[activeFolder].filter(item => item.id !== file?.id) }

      if (modalFolderItem[selectedFolder.id] == undefined) {
        newFolderItems[selectedFolder.id] = [file]
      }

      const data = expandPathUpUntilRoot(newFolderItems, selectedFolder.id)
      setBreadcrumbs(originalBreadcrumbs)
      setFolderItems(data)
    } else {
      const idArr = fileArr.map((val) => val.id)
      const activeFolder = fileArr[0].folders_id;
      const newFolderItems = { ...modalFolderItem, [activeFolder]: modalFolderItem[activeFolder].filter(item => !idArr.includes(item.id)) }
      const data = expandPathUpUntilRoot(newFolderItems, selectedFolder.id)
      setBreadcrumbs(originalBreadcrumbs)
      setFolderItems(data)
    }
    moveFileMutation.mutate()
  }

  const handleCancel = () => {
    setSelectedFolder({})
    setMoveFileModal(!moveFileModal)
  }

  const handleNavigateFolder = (folder: DictType) => {
    setCurrentFolder(folder)
    setSelectedFolder(folder)
    setMoveBreadcrumbs([...moveBreadcrumbs, folder])
    // refetchCurrent()
  }

  const handleBreadcrumbClick = (folder: DictType, index: number) => {
    setCurrentFolder(folder)
    setSelectedFolder(folder)
    setMoveBreadcrumbs(moveBreadcrumbs.slice(0, index + 1))
    refetchCurrent()
  }

  const handleBackClick = () => {
    if (moveBreadcrumbs.length > 1) {
      const parentFolder = moveBreadcrumbs[moveBreadcrumbs.length - 2]
      setCurrentFolder(parentFolder)
      setSelectedFolder(parentFolder)
      setMoveBreadcrumbs(moveBreadcrumbs.slice(0, -1))
      refetchCurrent()
    }
  }

  const handleSelectFolder = (folder: DictType) => setSelectedFolder(folder)

  const subFolders = currentFolderContent?.subfolders ?? []
  const isRootFolder = currentFolder?.id === (rootFolder?.id || parentFolder?.id)

  useEffect(() => {
    if (currentFolder && !selectedFolder?.id) {
      setSelectedFolder(currentFolder)
    }
  }, [currentFolder, selectedFolder])

  useEffect(() => {
    if (moveFileModal) {
      const root = rootFolder || parentFolder
      setCurrentFolder(root)
      setMoveBreadcrumbs([root])
      refetchSubFolders()
    }
  }, [moveFileModal])

  useEffect(() => {
    if (!isLoadingCurrent && isFetchedCurrent && currentFolderContent) {
      const { files, subfolders } = currentFolderContent
      const folderMetaData: { [id: number]: number } = { ...foldersMeta }
      const newBreadcrumbs = { ...originalBreadcrumbs }

      const rootFolderFiles = files?.items?.map((item: DictType) => {
        folderMetaData[item?.id] = currentFolder?.id
        return { ...item, as: "file", isExpanded: false }
      }) || []

      const rootFolderSubFolders = subfolders?.items?.map((item: DictType) => {
        folderMetaData[item?.id] = currentFolder?.id
        newBreadcrumbs[item?.id] = [...(newBreadcrumbs[currentFolder?.id] || []), item]
        return { ...item, as: "folder" }
      }) || []

      setFoldersMeta(folderMetaData)
      setOriginalBreadcrumbs(newBreadcrumbs)

      setModalFolderItem((prev) => {
        const existingItems = prev[currentFolder?.id] || []
        const newItems = [...rootFolderFiles, ...rootFolderSubFolders]
        const cutFileIds = new Set(cutFiles.map((file) => file.id))
        // Append new items, avoiding duplicates and filtering out moved files
        const existingItemsMap = Object.fromEntries(existingItems.map(item => [item.id, item]));

        const updatedNewItems = newItems.map(item => {
          const match = existingItemsMap[item.id];
          return match ? { ...item, isExpanded: match.isExpanded } : item;
        });
        const updatedItems = [
          ...updatedNewItems.filter((item) => !cutFileIds.has(item.id)),
          ...existingItems.filter(
            (item) => !cutFileIds.has(item.id) && !newItems.some((newItem) => newItem.id === item.id)
          ),
        ]
        return {
          ...prev,
          [currentFolder?.id]: updatedItems,
        }
      })
    }
  }, [isLoadingCurrent, isFetchedCurrent, currentFolderContent])

  return (
    <Dialog open={moveFileModal} onOpenChange={handleCancel}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="pb-1">
            {translate("Move")} {`"${fileArr.length == 0 ? file?.name : fileArr.length == 1 ? fileArr[0]?.name : "Files"}"`}
          </DialogTitle>
          {!isRootFolder && (
            <div className="flex items-center gap-1 mt-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBackClick}
                className="flex items-center"
              >
                <ArrowLeft size={20} />
              </Button>
              {currentFolder?.name || translate("Back")}
            </div>
          )}
          <DialogDescription className='flex min-h-20 gap-4 rounded-md border p-2'>
            <div className='min-h-[200px] max-h-[300px] overflow-y-auto p-2 w-full'>
              {(isLoadingCurrent || isFetchingCurrent) && <PmsLoader />}
              {!isLoadingCurrent && !isFetchingCurrent && isFetchedCurrent && (
                <>
                  {subFolders.length > 0 ? (
                    subFolders.map((folder: DictType) => (
                      <div
                        key={folder.id}
                        className={`flex items-center justify-between p-2 rounded-md group cursor-pointer ${selectedFolder?.id === folder.id ? 'bg-blue-100' : 'hover:bg-gray-100'}`}
                        onClick={() => handleSelectFolder(folder)}
                        onDoubleClick={() => handleNavigateFolder(folder)}
                      >
                        <div className="flex items-center gap-2">
                          <FolderIcon className="h-5 w-5 text-gray-500" />
                          <span className="text-sm">{folder.name}</span>
                        </div>
                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              setSelectedFolder(folder)
                              handleClick()
                            }}
                            disabled={moveFileMutation?.isPending}
                          >
                            {translate("Move")}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleNavigateFolder(folder)
                            }}
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <span className="text-sm text-muted-foreground">{translate("No subfolders found")}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <div className="flex items-center flex-wrap w-full mb-2">
            {moveBreadcrumbs.map((bread, index) => (
              <div key={bread.id} className="flex items-center">
                <Button
                  variant="link"
                  className="p-0 text-sm"
                  onClick={() => handleBreadcrumbClick(bread, index)}
                  disabled={index === moveBreadcrumbs.length - 1}
                >
                  {bread.name}
                </Button>
                {index < moveBreadcrumbs.length - 1 && <span className="mx-1">/</span>}
              </div>
            ))}
          </div>
          <div className="flex justify-end items-center gap-2">
            <DialogClose>
              <Button variant={"outline"} size={"sm"} onClick={handleCancel}>
                {translate("Cancel")}
              </Button>
            </DialogClose>
            <Button
              size={"sm"}
              variant={moveFileMutation?.isPending || !selectedFolder?.id ? "disabled" : "secondary"}
              disabled={moveFileMutation?.isPending || !selectedFolder?.id}
              onClick={handleClick}
            >
              {translate("Move")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default MoveFile
